import * as Data from './archibus-v2/DataApi'
import * as Document from './archibus/DocumentApi'

const generateSearchRestriction = ({restriction, searchValue, fields}) => {
  let lowerCaseSearch = `%${searchValue.toLowerCase()}%`
  let upperCaseSearch = `%${searchValue.toUpperCase()}%`

  fields.forEach((field, idx) => {
    restriction.addClause(field, lowerCaseSearch, 'LIKE', idx ? 'OR' : ')AND(')
    restriction.addClause(field, upperCaseSearch, 'LIKE', 'OR')
  })

  return restriction
}

export const fetchBuildingPrompt = ({searchValue = null} = {}) => {
  let restriction = null
  if (searchValue) {
    restriction = new Ab.view.Restriction()
    
    restriction.addClause('bl.ada_bldg','Yes')
    
    restriction = generateSearchRestriction({
      restriction,
      searchValue,
      fields: ['bl.bl_id', 'bl.name', 'city.name', 'city.state_id']
    })
  }

  let buildings = []

  return Data.getDataFromDataSource(
    'ab-self-service.axvw',
    'buildingPrompt',
    restriction
  ).then(result => {
    const {data} = result
    buildings = data
    let buildingsWithPhotos = data.reduce((acc, item) => {
      if (item.bldgPhoto) {
        acc.push(item.blId)
      }
      return acc
    }, [])

    return Promise.all(
      buildingsWithPhotos.map(blId => {
        return Document.getTempDocumentUrl(
          'bl',
          {bl_id: blId},
          'bldg_photo'
        ).then(url => {
          return Promise.resolve({blId, url})
        })
      })
    ).then(photoUrls => {
      let photoUrlObject = photoUrls.reduce((acc, item) => {
        acc[item.blId] = item
        return acc
      }, {})

      // Add the url to the buildings
      let result = buildings.map(building => {
        const {blId} = building
        building.url = photoUrlObject[blId] ? photoUrlObject[blId].url : null
        return building
      })

      return Promise.resolve(result)
    })
  })
}

export const fetchFloorPrompt = ({blId = null, searchValue = null} = {}) => {
  let restriction = null

  if (blId || searchValue) {
    restriction = new Ab.view.Restriction()
  }

  if (blId) {
    restriction.addClause('fl.bl_id', blId, '=')
  }

  if (searchValue) {
    restriction = generateSearchRestriction({
      restriction,
      searchValue,
      fields: ['fl.fl_id', 'bl.name', 'fl.name']
    })
  }

  return Data.getDataFromDataSource(
    'ab-self-service.axvw',
    'floorPrompt',
    restriction
  ).then(result => {
    const {data} = result
    return Promise.resolve(data)
  })
}

export const fetchRoomPrompt = ({
  blId = null,
  flId = null,
  searchValue
} = {}) => {
  let restriction

  if (blId || flId || searchValue) {
    restriction = new Ab.view.Restriction()
  }

  if (blId || flId) {
    if (blId) {
      restriction.addClause('rm.bl_id', blId)
    }
    if (flId) {
      restriction.addClause('rm.fl_id', flId)
    }
  }

  if (searchValue) {
    restriction = generateSearchRestriction({
      restriction,
      searchValue,
      fields: ['rm.rm_id', 'bl.name']
    })
  }

  return Data.getDataFromDataSource(
    'ab-self-service.axvw',
    'roomPrompt',
    restriction
  ).then(result => {
    const {data} = result
    return Promise.resolve(data)
  })
}

export const fetchEquipmentPrompt = ({blId, flId, rmId, searchValue}) => {
  let restriction
  if (blId || flId || rmId || searchValue) {
    restriction = new Ab.view.Restriction()
  }
  if (blId || flId || rmId) {
    if (blId) {
      restriction.addClause('eq.bl_id', blId)
    }
    if (flId) {
      restriction.addClause('eq.fl_id', flId)
    }
    if (rmId) {
      restriction.addClause('eq.rm_id', rmId)
    }
  }
  if (searchValue) {
    restriction = generateSearchRestriction({
      restriction,
      searchValue,
      fields: [
        'eq.eq_id',
        'eq.mfr',
        'eq.modelno',
        'eq.use1',
        'eq.eqstd',
        'eq.bl_id',
        'bl.name'
      ]
    })
  }

  return Data.getDataFromDataSource(
    'ab-self-service.axvw',
    'equipmentPrompt',
    restriction
  ).then(result => {
    const {data} = result
    return Promise.resolve(data)
  })
}

export const fetchEmployeePrompt = ({searchValue}) => {
  let restriction = null
  let employees = []
  if (searchValue) {
    restriction = new Ab.view.Restriction()
    restriction = generateSearchRestriction({
      restriction,
      searchValue,
      fields: ['em.em_id']
    })
  }

  return Data.getDataFromDataSource(
    'ab-self-service.axvw',
    'employeePrompt',
    restriction
  )
    .then(result => {
      const {data} = result
      employees = data
      let employeesWithPhotos = employees.filter(item => item.emPhoto)

      return Promise.all(
        employeesWithPhotos.map(employee => {
          const {emId} = employee
          return Document.getTempDocumentUrl(
            'em',
            {em_id: emId},
            'em_photo'
          ).then(url => {
            return Promise.resolve({em_id: emId, url})
          })
        })
      )
    })
    .then(photoUrls => {
      let indexedPhotoUrls = photoUrls.reduce((acc, item) => {
        acc[item.em_id] = item
        return acc
      }, {})

      // Add photo url
      let result = employees.map(employee => {
        const {emId} = employee
        employee.url = indexedPhotoUrls[emId]
          ? encodeURI(indexedPhotoUrls[emId].url)
          : null
        return employee
      })

      return Promise.resolve(result)
    })
}

export const fetchDepartmentPrompt = ({searchValue}) => {
  let restriction = null
  if (searchValue) {
    restriction = new Ab.view.Restriction()
    restriction = generateSearchRestriction({
      restriction,
      searchValue,
      fields: ['dv.dv_id', 'dp.dp_id', 'dp.name', 'dv.name']
    })
  }

  return Data.getDataFromDataSource(
    'ab-self-service.axvw',
    'departmentPrompt',
    restriction
  ).then(result => {
    const {data} = result
    return Promise.resolve(data)
  })
}
